import {EditableProps} from "../../../../../components/Editable/Editable";
import {IApiBusinessProfileProto, space} from "../../../../../provider_api";
import React, {useContext} from "react";
import {EditableContext, useEditable} from "../../../../../components/Editable/EditableContext";
import {priceToString} from "../../../../../util/PriceUtil";
import {EditablePriceField} from "../../../../../components/Editable/EditablePriceField";
import {filterErrors, findError} from "../../../../../components/input/InputTypes";
import {EditableSingleSelect} from "../../../../../components/Editable/EditableSingleSelect";
import IPaymentPolicyProto = space.kenko.proto.IPaymentPolicyProto;
import PrepaidPaymentPolicyProto = space.kenko.proto.PrepaidPaymentPolicyProto;
import PostpaidPaymentPolicyProto = space.kenko.proto.PostpaidPaymentPolicyProto;
import {useProviderProfile} from "../../../../../ProviderProfileProvider";

type Props<EnclosingType extends object> = EditableProps<EnclosingType, IPaymentPolicyProto> & {
  profile: IApiBusinessProfileProto | null | undefined, // for looking up price categories
}

/**
 * An editor for PaymentPolicy. This creates a sub-context for EditableContext in 
 * order to nest other Editables.
 */
export const EditablePaymentPolicy = <EnclosingType extends object>(props: Props<EnclosingType>) => {

  const {mode, errors, editing, value, setValue} = useEditable(props);

  const {businessProfile} = useProviderProfile();
  
  const getHelpText = () => {
    if (!value) {
      return undefined;
    }
    
    if (value.prepaid) {
      return 'Prepaid: clients are required to pay in advance by purchasing a single credit or a package.';
    } else if (value.postpaid) {
      return 'Postpaid: clients pay outside of Kensu once the service has been completed. Note that you are ' +
          'responsible for collecting payment. You can mark the service as "Manually paid" on the booking.';
    } else {
      return undefined;
    }
  }

  const options = [
    {value: 'prepaid', label: 'Prepaid'},
    {value: 'postpaid', label: 'Postpaid'},
  ]

  const priceCategoryOptions = props.profile?.pricing?.priceCategories?.filter(pc => !pc.archived).map(category => {
    return {
      label: `${category.displayName} (${priceToString(category.singleClassPrice, true)})`,
      value: category.categoryId as string
    }
  }) ?? [];

  const paymentPolicyError = findError(errors, 'paymentPolicy');
  
  return <>

    {/* This is a nested editor, so we update the context to the the field we are drilling into, in this
        case the PaymentPolicy. */}
    <EditableContext.Provider value={{
      mode: mode,
      editing: editing,
      errors: filterErrors(errors, 'paymentPolicy.'),
      workingCopy: value,
      setWorkingCopy: setValue
    }}>

      <EditableSingleSelect<IPaymentPolicyProto, string>
          label={'Payment Policy'}
          helpText={getHelpText()}
          errorKey={'type'} // this matches server implementation
          options={options}
          getter={entity => entity.prepaid ? 'prepaid' : (entity.postpaid ? 'postpaid' : undefined)}
          setter={(paymentPolicy, value) => {
            
            if (value == 'prepaid') {
              return {
                ...paymentPolicy,
                prepaid: new PrepaidPaymentPolicyProto(),
                postpaid: undefined
              }
            } else if (value == 'postpaid') {
              return {
                ...paymentPolicy,
                prepaid: undefined,
                postpaid: new PostpaidPaymentPolicyProto({
                  price: {
                    currencyCode: 'USD'
                  }
                })
              }
            } else {
              return {
                ...paymentPolicy,
                prepaid: undefined,
                postpaid: undefined,
              };
            }
          }}/>

      {paymentPolicyError && <div className={'ErrorText'}>{paymentPolicyError}</div>}
      

      {value?.postpaid && <EditablePriceField<IPaymentPolicyProto>
          field='postpaid'
          getter={paymentPolicy => paymentPolicy.postpaid?.price}
          setter={(paymentPolicy, value) => {
            return {
              ...paymentPolicy,
              postpaid: {
                ...paymentPolicy.postpaid,
                price: value
              }
            }
          }}
          errorKey={'postpaid.price'}
          label={'Price (USD)'}
      />}


      {value?.prepaid && <EditablePriceField<IPaymentPolicyProto>
          field='prepaid'
          getter={paymentPolicy => paymentPolicy.prepaid?.price}
          setter={(paymentPolicy, value) => {
            return {
              ...paymentPolicy,
              prepaid: {
                ...paymentPolicy.prepaid,
                price: value
              }
            }
          }}
          errorKey={'prepaid.price'}
          label={'Price (USD)'}
      />}

    </EditableContext.Provider>

  </>
};